import { Money, parseLocalDate, type LocalDate } from "@despejo/domain";

/**
 * Manual ledger CSV import (plan §7 "Manual transaction entry and CSV
 * import"). Tolerates what Costa Rican spreadsheets actually export:
 * `;` or `,` delimiters, decimal commas ("1.234,56"), currency signs,
 * DD/MM/YYYY dates, and Spanish yes/no.
 *
 * Columns (header names, case/accents insensitive):
 *   fecha, descripcion, contraparte, tipo (ingreso|gasto), monto (total),
 *   iva (optional), moneda (CRC|USD|EUR, optional), comprobante (si/no, optional)
 */

export interface CsvTransaction {
  row: number;
  document_date: LocalDate;
  description: string;
  counterparty_name: string | null;
  direction: "INCOME" | "EXPENSE";
  total: string;
  tax_amount: string;
  currency: "CRC" | "USD" | "EUR";
  has_electronic_receipt: boolean;
}

export interface CsvResult {
  rows: CsvTransaction[];
  errors: { row: number; message: string }[];
}

function norm(h: string): string {
  return h
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

const ALIASES: Record<string, string> = {
  fecha: "fecha",
  date: "fecha",
  descripcion: "descripcion",
  detalle: "descripcion",
  concepto: "descripcion",
  contraparte: "contraparte",
  proveedor: "contraparte",
  cliente: "contraparte",
  tipo: "tipo",
  monto: "monto",
  total: "monto",
  iva: "iva",
  impuesto: "iva",
  moneda: "moneda",
  comprobante: "comprobante",
  tienecomprobante: "comprobante",
  factura: "comprobante",
};

/** Split one CSV line honoring double quotes. */
export function splitLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (q) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') q = false;
      else cur += ch;
    } else if (ch === '"') q = true;
    else if (ch === delim) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/** "₡1.234.567,89" / "1,234,567.89" / "1 234 567,89" / "(500)" -> exact decimal string. */
export function parseAmount(raw: string): string | null {
  let s = raw.trim().replace(/US\$|[₡$€\s]|CRC|USD|EUR/gi, "");
  if (s === "") return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) {
    neg = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith("-")) {
    neg = true;
    s = s.slice(1);
  }
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma > lastDot) {
    // decimal comma: dots (or nothing) are thousands separators
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    s = s.replace(/,/g, "");
  }
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const m = Money.of(s);
  return (neg ? m.neg() : m).toString();
}

export function parseDate(raw: string): LocalDate | null {
  const s = raw.trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  let y: number, mo: number, d: number;
  if (m) [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  else if ((m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s))) [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
  else return null;
  const iso = `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  try {
    parseLocalDate(iso);
    return iso;
  } catch {
    return null;
  }
}

function yes(raw: string | undefined): boolean {
  return /^(si|sí|s|yes|y|1|true|x)$/i.test((raw ?? "").trim());
}

export function parseTransactionsCsv(text: string): CsvResult {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim() !== "");
  const errors: CsvResult["errors"] = [];
  if (lines.length < 2) return { rows: [], errors: [{ row: 0, message: "El archivo no tiene filas de datos" }] };
  const header = lines[0]!;
  const delim = (header.match(/;/g)?.length ?? 0) > (header.match(/,/g)?.length ?? 0) ? ";" : ",";
  const cols = splitLine(header, delim).map((h) => ALIASES[norm(h)] ?? null);
  for (const need of ["fecha", "descripcion", "tipo", "monto"]) {
    if (!cols.includes(need)) errors.push({ row: 1, message: `Falta la columna "${need}"` });
  }
  if (errors.length) return { rows: [], errors };
  const rows: CsvTransaction[] = [];
  lines.slice(1).forEach((line, i) => {
    const row = i + 2;
    const cells = splitLine(line, delim);
    const get = (k: string) => {
      const idx = cols.indexOf(k);
      return idx >= 0 ? cells[idx] : undefined;
    };
    const date = parseDate(get("fecha") ?? "");
    if (!date) return errors.push({ row, message: `Fecha inválida: "${get("fecha") ?? ""}"` });
    const tipo = norm(get("tipo") ?? "");
    const direction = tipo.startsWith("ing") || tipo === "venta" ? "INCOME" : tipo.startsWith("gas") || tipo === "compra" ? "EXPENSE" : null;
    if (!direction) return errors.push({ row, message: `Tipo debe ser "ingreso" o "gasto": "${get("tipo") ?? ""}"` });
    const total = parseAmount(get("monto") ?? "");
    if (total === null) return errors.push({ row, message: `Monto inválido: "${get("monto") ?? ""}"` });
    const ivaRaw = get("iva");
    const tax = ivaRaw && ivaRaw.trim() !== "" ? parseAmount(ivaRaw) : "0";
    if (tax === null) return errors.push({ row, message: `IVA inválido: "${ivaRaw}"` });
    const cur = (get("moneda") ?? "CRC").trim().toUpperCase() || "CRC";
    if (!["CRC", "USD", "EUR"].includes(cur)) return errors.push({ row, message: `Moneda no soportada: "${cur}"` });
    const description = (get("descripcion") ?? "").trim();
    if (!description) return errors.push({ row, message: "Falta la descripción" });
    rows.push({
      row,
      document_date: date,
      description,
      counterparty_name: (get("contraparte") ?? "").trim() || null,
      direction,
      total,
      tax_amount: tax,
      currency: cur as CsvTransaction["currency"],
      has_electronic_receipt: yes(get("comprobante")),
    });
  });
  return { rows, errors };
}
